import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Form, Row, Col, Spinner, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ContabilidadContext } from '../../common/ContabilidadContext';
import { ContabilidadLayout } from '../../common/ContabilidadLayout';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const FormularioCuentaBancariaContent = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();
    const { selectedCompany } = useContext(ContabilidadContext);

    const idDatoFiscalSeleccionado = selectedCompany?.idDatoFiscal || null;

    const [vNombreBanco, setNombreBanco] = useState('');
    const [vAlias, setAlias] = useState('');
    const [vNumCta, setNumCta] = useState('');
    const [vClabe, setClabe] = useState('');
    const [vNombreTitular, setNombreTitular] = useState('');
    const [idCatMoneda, setIdCatMoneda] = useState('');
    
    const [guardando, setGuardando] = useState(false);
    const [cargandoDatos, setCargandoDatos] = useState(false);
    const [monedas, setMonedas] = useState([]);

    useEffect(() => {
        cargarMonedas();
        if (id) {
            cargarDatosEdicion();
        }
    }, [id]);

    const cargarDatosEdicion = async () => {
        if (location.state?.cuenta) {
            const c = location.state.cuenta;
            setNombreBanco(c.vNombreBanco || '');
            setAlias(c.vAlias || '');
            setNumCta(c.vNumCta || '');
            setClabe(c.vClabe || '');
            setNombreTitular(c.vNombreTitular || '');
            setIdCatMoneda(c.idCatMoneda || '');
        } else if (idDatoFiscalSeleccionado) {
            // Si el usuario recargó la página y se perdió el state, intentamos buscarla en ObtenerCuentas
            setCargandoDatos(true);
            try {
                const response = await fetch('/api/contabilidad/bancos/CuentasBancarias/ObtenerCuentas', {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ idDatoFiscal: idDatoFiscalSeleccionado })
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.iResponseType === 1) {
                        const parsed = JSON.parse(data.detalles);
                        const arr = Array.isArray(parsed) ? parsed : (parsed.Table1 || parsed.Table || Object.values(parsed)[0] || []);
                        const c = arr.find(x => x.idCatCuentaBancaria === parseInt(id));
                        if (c) {
                            setNombreBanco(c.vNombreBanco || '');
                            setAlias(c.vAlias || '');
                            setNumCta(c.vNumCta || '');
                            setClabe(c.vClabe || '');
                            setNombreTitular(c.vNombreTitular || '');
                            setIdCatMoneda(c.idCatMoneda || '');
                        } else {
                            showErrorToast("No se encontró la cuenta.");
                        }
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                setCargandoDatos(false);
            }
        }
    };

    const cargarMonedas = async () => {
        try {
            const response = await fetch('/api/contabilidad/CatalogoSAT/ObtenerCatMonedas', {
                method: 'POST',
                headers: { 'Authorization': 'Bearer ' + token }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const parsed = JSON.parse(data.detalles);
                    const arrayMonedas = Array.isArray(parsed) 
                        ? parsed 
                        : (parsed.Table1 || parsed.Table || Object.values(parsed)[0] || []);
                    setMonedas(arrayMonedas);
                }
            }
        } catch (error) {
            console.error("Error al cargar monedas", error);
        }
    };

    const handleSave = async () => {
        if (!idDatoFiscalSeleccionado) {
            showErrorToast('Por favor selecciona una empresa (Dato Fiscal) en el menú superior.');
            return;
        }

        if (!vNombreBanco || !vNumCta || !idCatMoneda) {
            showErrorToast('Por favor, completa los campos obligatorios (Banco, Número de Cuenta y Moneda).');
            return;
        }

        setGuardando(true);
        const endpoint = id 
            ? '/api/contabilidad/bancos/CuentasBancarias/EditarCuenta' 
            : '/api/contabilidad/bancos/CuentasBancarias/CrearCuenta';

        const payload = {
            idDatoFiscal: idDatoFiscalSeleccionado,
            idCatMoneda: parseInt(idCatMoneda),
            vNumCta: vNumCta,
            vClabe: vClabe,
            vNombreBanco: vNombreBanco,
            vNombreTitular: vNombreTitular,
            vAlias: vAlias
        };

        if (id) {
            payload.idCatCuentaBancaria = parseInt(id);
        }

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    showSuccessToast(id ? 'Cuenta actualizada correctamente.' : 'Cuenta creada correctamente.');
                    navigate('/contabilidad/bancos/cuentas-bancarias');
                } else {
                    showErrorToast(data.vMessage || 'Ocurrió un error al guardar la cuenta.');
                }
            } else {
                showErrorToast('Error de red al guardar la cuenta.');
            }
        } catch (error) {
            console.error(error);
            showErrorToast('Ocurrió un error al procesar la solicitud.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Row className="justify-content-center">
            <Col xs={12} xl={10} xxl={8}>
                <Card className="shadow-sm border-0 rounded-3">
                    <Card.Header className="bg-white border-bottom py-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 text-dark fw-bold d-flex align-items-center">
                                <div className="bg-primary bg-opacity-10 text-primary rounded d-flex justify-content-center align-items-center me-3" style={{ width: '40px', height: '40px' }}>
                                    <FontAwesomeIcon icon={id ? 'edit' : 'university'} />
                                </div>
                                {id ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}
                            </h5>
                            <Button variant="light" size="sm" onClick={() => navigate('/contabilidad/bancos/cuentas-bancarias')} className="text-secondary fw-bold">
                                <FontAwesomeIcon icon="arrow-left" className="me-2" /> Regresar
                            </Button>
                        </div>
                    </Card.Header>
                    <Card.Body className="p-4 p-md-5">
                        {cargandoDatos ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-3 text-muted fw-medium">Cargando información de la cuenta...</p>
                            </div>
                        ) : (
                            <Form>
                                <div className="mb-4">
                                    <h6 className="fw-bold text-primary mb-3 border-bottom pb-2">Información Principal</h6>
                                    <Row className="g-3">
                                        <Col xs={12} md={6}>
                                            <Form.Group>
                                                <Form.Label className="text-700 mb-1">Banco <span className="text-danger">*</span></Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text>
                                                        <FontAwesomeIcon icon="building-columns" />
                                                    </InputGroup.Text>
                                                    <Form.Control 
                                                        type="text" 
                                                        value={vNombreBanco} 
                                                        onChange={(e) => setNombreBanco(e.target.value)} 
                                                        placeholder="Ej. BBVA, Santander"
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Group>
                                                <Form.Label className="text-700 mb-1">Alias (Identificador)</Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text>
                                                        <FontAwesomeIcon icon="tag" />
                                                    </InputGroup.Text>
                                                    <Form.Control 
                                                        type="text" 
                                                        value={vAlias} 
                                                        onChange={(e) => setAlias(e.target.value)} 
                                                        placeholder="Ej. BBVA Principal"
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Group>
                                                <Form.Label className="text-700 mb-1">Moneda <span className="text-danger">*</span></Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text>
                                                        <FontAwesomeIcon icon="coins" />
                                                    </InputGroup.Text>
                                                    <Form.Select 
                                                        value={idCatMoneda} 
                                                        onChange={(e) => setIdCatMoneda(e.target.value)}
                                                    >
                                                        <option value="">Seleccione...</option>
                                                        {monedas.map(m => (
                                                            <option key={m.idCatMoneda} value={m.idCatMoneda}>
                                                                {m.vClaveMoneda}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>

                                <div className="mb-4 mt-5">
                                    <h6 className="fw-bold text-primary mb-3 border-bottom pb-2">Detalles de la Cuenta</h6>
                                    <Row className="g-3">
                                        <Col xs={12} md={6}>
                                            <Form.Group>
                                                <Form.Label className="text-700 mb-1">Número de Cuenta <span className="text-danger">*</span></Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text>
                                                        <FontAwesomeIcon icon="hashtag" />
                                                    </InputGroup.Text>
                                                    <Form.Control 
                                                        type="text" 
                                                        value={vNumCta} 
                                                        onChange={(e) => setNumCta(e.target.value)} 
                                                        placeholder="Ingrese los dígitos"
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Group>
                                                <Form.Label className="text-700 mb-1">CLABE</Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text>
                                                        <FontAwesomeIcon icon="key" />
                                                    </InputGroup.Text>
                                                    <Form.Control 
                                                        type="text" 
                                                        value={vClabe} 
                                                        onChange={(e) => setClabe(e.target.value)} 
                                                        placeholder="18 dígitos"
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12}>
                                            <Form.Group>
                                                <Form.Label className="text-700 mb-1">Nombre del Titular</Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text>
                                                        <FontAwesomeIcon icon="user" />
                                                    </InputGroup.Text>
                                                    <Form.Control 
                                                        type="text" 
                                                        value={vNombreTitular} 
                                                        onChange={(e) => setNombreTitular(e.target.value)} 
                                                        placeholder="Razón Social o Persona Física"
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>

                                <hr className="my-4 text-muted" />
                                
                                <div className="d-flex justify-content-end gap-2">
                                    <Button variant="light" className="px-4 text-secondary fw-bold shadow-sm" onClick={() => navigate('/contabilidad/bancos/cuentas-bancarias')} disabled={guardando}>
                                        Cancelar
                                    </Button>
                                    <Button variant="primary" className="px-5 shadow-sm fw-bold" onClick={handleSave} disabled={guardando}>
                                        {guardando ? (
                                            <>
                                                <Spinner size="sm" animation="border" className="me-2" /> Guardando...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon="save" className="me-2" />
                                                Guardar Cuenta
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

const FormularioCuentaBancaria = () => {
    return (
        <ContabilidadLayout
            title={
                <><i className="fas fa-university me-2" />Cuentas Bancarias</>
            }
        >
            <FormularioCuentaBancariaContent />
        </ContabilidadLayout>
    );
};

export default FormularioCuentaBancaria;
